import { Grid, styled, useTheme } from "@mui/material";
import AcxInputLabel from "components/UI/AcxInputLabel";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxSelectMulti from "components/UI/Select/BaseSelectComponents/AcxSelectMulti";
import React, { useEffect } from "react";
import { Organization } from "../../types/Organization.type";
import { ApplicationUser } from "models/Permission/ApplicationUser";
import { FormFieldErrors } from "shared/types/FormErrors.type";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import AcxHierarchySelector from "components/UI/AcxHierarchySelector/AcxHierarchySelector";

const PaddingRightGrid = styled(Grid)({
    paddingRight: "0.8rem!important",
});

const ZeroCallsSettings = ({
    isLoading,
    org,
    updateOrg,
    fieldErrors,
    users,
    usersLoading,
    userId,
    orgId,
}: {
    isLoading: boolean;
    org: Organization | null | undefined;
    updateOrg: (
        val: string | number | boolean | string[],
        prop: keyof Organization,
    ) => void;
    fieldErrors: FormFieldErrors | undefined;
    users: ApplicationUser[] | null | undefined;
    usersLoading: boolean;
    userId: string;
    orgId: string;
}) => {
    const theme = useTheme();

    useEffect(() => {
        if (org?.calendarOrBusinessDays === undefined) {
            updateOrg("Calendar", "calendarOrBusinessDays");
        }
        if (org?.interactionsWithinDays === undefined) {
            updateOrg(0, "interactionsWithinDays");
        }
        if (org?.dailyInteractionAlertUserIds === undefined) {
            updateOrg([], "dailyInteractionAlertUserIds");
        }
        if (org?.hierarchyIdsToAlertOnZeroInteractions === undefined) {
            updateOrg([], "hierarchyIdsToAlertOnZeroInteractions");
        }
    }, [org, updateOrg]);

    return (
        <>
            <PaddingRightGrid item xs={6} sx={{ my: 2 }}>
                <Grid>
                    <AcxInputLabel
                        sx={{ color: theme.palette.black.main }}
                        labelText="Select hierarchies for volume notifications:"
                        infoText="When selected hierarchies receive 0 interactions within the defined time period, users will be notified via email."
                    />
                    <Grid sx={{ mt: 1 }}>
                        <AcxHierarchySelector
                            displayType="input"
                            userId={userId}
                            orgId={orgId}
                            setHierarchyIds={(ids) => {
                                updateOrg(
                                    ids,
                                    "hierarchyIdsToAlertOnZeroInteractions",
                                );
                            }}
                            onSaveUpdateWithHierarchies={() => null}
                            initSelectedHierarchyIds={
                                org?.hierarchyIdsToAlertOnZeroInteractions || []
                            }
                        />
                    </Grid>
                    <AcxInputLabel
                        sx={{ color: theme.palette.black.main }}
                        labelText="When one of the selected hierarchies receives 0 interactions within:"
                        infoText="If business day(s) is selected, volume checks will only run on business days (M-F, excluding public holidays)."
                    />
                    <Grid
                        item
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            mt: 1,
                            flexDirection: "row",
                        }}
                    >
                        <AcxMainTextField
                            skeleton={isLoading}
                            onChange={(e) =>
                                updateOrg(
                                    e.target.value,
                                    "interactionsWithinDays",
                                )
                            }
                            type="number"
                            id="interactionsWithinDays"
                            value={org?.interactionsWithinDays}
                            error={!!fieldErrors?.interactionsWithinDays}
                            helperText={fieldErrors?.interactionsWithinDays}
                            showAllErrors={true}
                            labelText=""
                            sx={{
                                maxWidth: "70px",
                            }}
                        />
                        <AcxSelectSingle<{ value: string; label: string }>
                            id="calendarOrBusinessDays"
                            options={[
                                { value: "Calendar", label: "calendar day(s)" },
                                { value: "Business", label: "business day(s)" },
                            ]}
                            valueField="value"
                            labelField="label"
                            defaultValue={{
                                value:
                                    org?.calendarOrBusinessDays || "Calendar",
                                label:
                                    org?.calendarOrBusinessDays === "Business"
                                        ? "business day(s)"
                                        : "calendar day(s)",
                            }}
                            onChange={(e) =>
                                updateOrg(
                                    e ? e.value : "Calendar",
                                    "calendarOrBusinessDays",
                                )
                            }
                            fullWidth
                        />
                    </Grid>
                </Grid>
                <Grid sx={{ mt: 1 }}>
                    <AcxSelectMulti
                        id="dailyInteractionAlertUserIds"
                        options={users ?? []}
                        skeleton={isLoading || usersLoading}
                        defaultValue={org?.dailyInteractionAlertUserIds?.map(
                            (id) => users?.find((user) => id === user.id),
                        )}
                        onChange={(e) =>
                            updateOrg(
                                e ? e.map((user) => user?.id || "") : [],
                                "dailyInteractionAlertUserIds",
                            )
                        }
                        valueField={"id"}
                        labelField={"userName"}
                        containerHeight="auto"
                        fullWidth
                        isClearable
                        inputLabel="Notify the following users:"
                    />
                </Grid>
            </PaddingRightGrid>
            <PaddingRightGrid item xs={6} sx={{ my: 2 }}></PaddingRightGrid>
        </>
    );
};

export default ZeroCallsSettings;
